import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { AlertTriangle, Check, Copy, Eye, Loader2, Mail, RefreshCw, Send, Trash2, UserCheck, UserX } from 'lucide-react';

export default function IdentifiantsActionDialog({
  actionDialog, setActionDialog, actionLoading, copiedField, copyToClipboard,
  getRoleConfig, handleDeleteUser, handleResetPassword, handleSendCredentials, handleUpdateStatus
}) {
  return (
      <Dialog open={actionDialog.open} onOpenChange={() => setActionDialog({ open: false, type: null, user: null })}>
        <DialogContent>
          {actionDialog.type === 'send' && actionDialog.user && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-600" />
                  Envoyer les identifiants
                </DialogTitle>
                <DialogDescription>
                  Un email contenant les identifiants de connexion sera envoyé à <strong>{actionDialog.user.email}</strong>
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <p className="text-sm text-gray-600 mb-4">
                  Un nouveau mot de passe sera généré et envoyé par email. L&apos;utilisateur devra le changer à sa première connexion.
                </p>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="font-medium">{actionDialog.user.prenom} {actionDialog.user.nom}</p>
                  <p className="text-sm text-gray-600">{actionDialog.user.email}</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null, user: null })}>
                  Annuler
                </Button>
                <Button onClick={() => handleSendCredentials(actionDialog.user)} disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Envoyer
                </Button>
              </DialogFooter>
            </>
          )}

          {actionDialog.type === 'reset' && actionDialog.user && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-orange-600" />
                  Renouveler le mot de passe
                </DialogTitle>
                <DialogDescription>
                  Générer un nouveau mot de passe pour <strong>{actionDialog.user.email}</strong>
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <p className="font-medium">{actionDialog.user.prenom} {actionDialog.user.nom}</p>
                  <p className="text-sm text-gray-600">{actionDialog.user.email}</p>
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null, user: null })}>
                  Annuler
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => handleResetPassword(actionDialog.user, false)} 
                  disabled={actionLoading}
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                  Générer sans envoyer
                </Button>
                <Button onClick={() => handleResetPassword(actionDialog.user, true)} disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                  Générer et envoyer
                </Button>
              </DialogFooter>
            </>
          )}

          {actionDialog.type === 'show-password' && actionDialog.data && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-green-600">
                  <Check className="h-5 w-5" />
                  Nouveau mot de passe généré
                </DialogTitle>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-sm text-gray-600 mb-1">Email</p>
                  <div className="flex items-center justify-between">
                    <span className="font-mono">{actionDialog.user?.email}</span>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(actionDialog.user?.email, 'email')}>
                      {copiedField === 'email' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-600 mb-1">Nouveau mot de passe</p>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-lg font-bold">{actionDialog.data.nouveau_mot_de_passe}</span>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(actionDialog.data.nouveau_mot_de_passe, 'password')}>
                      {copiedField === 'password' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                {actionDialog.data.email_envoye && (
                  <p className="text-sm text-green-600 flex items-center gap-2">
                    <Check className="h-4 w-4" /> Email envoyé avec succès
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button onClick={() => setActionDialog({ open: false, type: null, user: null })}>
                  Fermer
                </Button>
              </DialogFooter>
            </>
          )}

          {actionDialog.type === 'status' && actionDialog.user && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserCheck className="h-5 w-5 text-blue-600" />
                  Modifier le statut
                </DialogTitle>
                <DialogDescription>
                  Changer le statut de <strong>{actionDialog.user.email}</strong>
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start bg-green-50 hover:bg-green-100 border-green-200"
                  onClick={() => handleUpdateStatus(actionDialog.user, 'actif')}
                  disabled={actionLoading}
                >
                  <UserCheck className="h-4 w-4 mr-2 text-green-600" />
                  Activer le compte
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start bg-gray-50 hover:bg-gray-100"
                  onClick={() => handleUpdateStatus(actionDialog.user, 'inactif')}
                  disabled={actionLoading}
                >
                  <UserX className="h-4 w-4 mr-2 text-gray-600" />
                  Désactiver le compte
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start bg-red-50 hover:bg-red-100 border-red-200"
                  onClick={() => handleUpdateStatus(actionDialog.user, 'suspendu')}
                  disabled={actionLoading}
                >
                  <AlertTriangle className="h-4 w-4 mr-2 text-red-600" />
                  Suspendre le compte
                </Button>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null, user: null })}>
                  Annuler
                </Button>
              </DialogFooter>
            </>
          )}

          {actionDialog.type === 'delete' && actionDialog.user && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <Trash2 className="h-5 w-5" />
                  Supprimer l&apos;utilisateur
                </DialogTitle>
                <DialogDescription>
                  Cette action est irréversible. Toutes les données associées seront supprimées.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="font-medium text-red-800">{actionDialog.user.prenom} {actionDialog.user.nom}</p>
                  <p className="text-sm text-red-600">{actionDialog.user.email}</p>
                  <Badge className="mt-2">{getRoleConfig(actionDialog.user.role).label}</Badge>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null, user: null })}>
                  Annuler
                </Button>
                <Button variant="destructive" onClick={() => handleDeleteUser(actionDialog.user)} disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Supprimer définitivement
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
  );
}
